import { getQuestionTypeMeta, getRenderFamily } from './applicationQuestionTypes';

const DEFAULT_DECLARATION_1 = {
  mode: 'print',
  printNote:
    'Fill in the fields below online where enabled. Print/save the PDF, sign and add the company seal in the Signature box, then re-upload it where required.',
  fields: [
    { key: 'name', label: 'Name (Head of the Sales/HR/Marketing):', inputType: 'text', readOnly: true, required: false, rows: 1 },
    { key: 'designation', label: 'Designation', inputType: 'text', readOnly: true, required: false, rows: 1 },
    { key: 'email', label: 'Email Address:', inputType: 'text', readOnly: true, required: false, rows: 1 },
    { key: 'signature', label: 'Signature with the Seal:', inputType: 'textarea', readOnly: true, required: false, rows: 6 },
  ],
};

const DEFAULT_DECLARATION_2 = {
  mode: 'online',
  printNote: '',
  fields: [
    {
      key: 'agreed',
      label: 'I acknowledge and agree to the above declaration',
      inputType: 'checkbox',
      readOnly: false,
      required: true,
      rows: 1,
    },
    {
      key: 'date',
      label: 'Date of Acknowledgment',
      inputType: 'date',
      readOnly: false,
      required: true,
      rows: 1,
    },
  ],
};

const DEFAULT_DECLARATION_3 = {
  mode: 'print',
  printNote:
    'Please print this form, fill in the fields below by hand, and re-upload the signed copy where required.',
  fields: [
    { key: 'name', label: 'Name:', inputType: 'text', readOnly: true, required: false, rows: 1 },
    { key: 'date', label: 'Date:', inputType: 'date', readOnly: true, required: false, rows: 1 },
  ],
};

const DEFAULT_TABLE_ROWS = {
  columns: [
    { key: 'item', label: 'Item' },
    { key: 'value', label: 'Value' },
  ],
  rowStyle: 'lettered',
};

const DEFAULT_DROPDOWN_GRID = {
  columns: [
    {
      key: 'agency_role',
      label: 'Agency Role',
      type: 'dropdown',
      uniqueAcrossRows: true,
      options: [
        'Lead Agency 1',
        'Lead Agency 2',
        'Contributing Agency 1',
        'Contributing Agency 2',
        'Contributing Agency 3',
        'Contributing Agency 4',
      ],
    },
    { key: 'agency_name', label: 'Agency Name', type: 'text' },
    { key: 'coordinator_name', label: 'Coordinator Name', type: 'text' },
    { key: 'contact_number', label: 'Contact Number', type: 'text' },
    { key: 'email', label: 'Email', type: 'text' },
  ],
  rowStyle: 'numbered',
  rowCount: 6,
  minRows: 1,
  maxRows: 6,
};

const DEFAULT_DYNAMIC_TABLE = {
  columns: [
    { key: 'name', label: 'Name', type: 'text' },
    { key: 'designation', label: 'Designation', type: 'text' },
    { key: 'contact_number', label: 'Contact Number', type: 'text' },
    { key: 'email', label: 'Email', type: 'text' },
  ],
  rowStyle: 'numbered',
  allowAddRows: true,
  dynamicRows: true,
  rowCount: 1,
  defaultRows: 1,
  minRows: 1,
  maxRows: 15,
};

const DEFAULT_DATA_GRID = {
  columns: [
    { key: 'col1', label: 'Column 1' },
    { key: 'col2', label: 'Column 2' },
    { key: 'col3', label: 'Column 3' },
  ],
  rowStyle: 'numbered',
  minRows: 1,
  maxRows: 20,
};

const DEFAULT_SIGNATURE_PAD = {
  penColor: '#111827',
  backgroundColor: '#ffffff',
  height: 160,
  instruction: 'Sign inside the box below',
};

const DEFAULT_SIDE_BY_SIDE_CHOICE = {
  columns: [
    { key: 'col1', label: 'Column 1' },
    { key: 'col2', label: 'Column 2' },
  ],
  columnOnlyOptions: {},
};

const DEFAULT_BY_TYPE = {
  DECLARATION_FORMAT_1: DEFAULT_DECLARATION_1,
  DECLARATION_FORMAT_2: DEFAULT_DECLARATION_2,
  DECLARATION_FORMAT_3: DEFAULT_DECLARATION_3,
  DECLARATION_CUSTOM: { mode: 'online', printNote: '', fields: [] },
  CONSENT_CHECKBOX: DEFAULT_DECLARATION_2,
  TERMS_ACCEPTANCE: DEFAULT_DECLARATION_2,
  PRIVACY_NOTICE: DEFAULT_DECLARATION_2,
  TABLE_ROWS: DEFAULT_TABLE_ROWS,
  DATA_GRID: DEFAULT_DATA_GRID,
  DROPDOWN_GRID: DEFAULT_DROPDOWN_GRID,
  DYNAMIC_TABLE: DEFAULT_DYNAMIC_TABLE,
  COMPARISON_TABLE: DEFAULT_DATA_GRID,
  SIDE_BY_SIDE_CHOICE: DEFAULT_SIDE_BY_SIDE_CHOICE,
  SIGNATURE_PAD: DEFAULT_SIGNATURE_PAD,
  WITNESS_SIGNATURE: DEFAULT_SIGNATURE_PAD,
  SIGNATURE_UPLOAD: { accept: 'image/*', maxSizeMB: 5 },
  LIKERT: { scaleLabels: ['Strongly disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly agree'] },
  NPS: { min: 0, max: 10 },
  RATING: { max: 5 },
  STAR_RATING_HALF: { max: 5, allowHalf: true },
  SLIDER: { min: 0, max: 100, step: 1 },
  RANGE: { min: 0, max: 100, step: 1 },
  SCALE_1_10: { min: 1, max: 10 },
  MATRIX_SINGLE: { columns: ['Col 1', 'Col 2', 'Col 3'] },
  MATRIX_MULTI: { columns: ['Col 1', 'Col 2', 'Col 3'] },
  OPINION_SCALE: { scaleLabels: ['Poor', 'Fair', 'Good', 'Very good', 'Excellent'] },
  REPEATING_SECTION: { minRows: 1, maxRows: 10 },
  TEXT_WITH_FILE: { inputStyle: 'textarea', attachmentLabel: 'Attachment' },
  SINGLE_CHOICE_OTHER: { otherOptionLabel: 'Other', otherPlaceholder: 'Please specify…' },
  MULTIPLE_CHOICE_OTHER: { otherOptionLabel: 'Other', otherPlaceholder: 'Please specify…' },
  DROPDOWN_OTHER: { otherOptionLabel: 'Other', otherPlaceholder: 'Please specify…' },
  PIPED_DISPLAY: { sourceFieldKey: '', emptyMessage: '—' },
};

export function getDefaultTypeConfig(type) {
  const key = String(type || '').toUpperCase();
  const base = DEFAULT_BY_TYPE[key];
  if (!base) return {};
  return JSON.parse(JSON.stringify(base));
}

function mergeFields(defaultFields = [], customFields = []) {
  if (!Array.isArray(customFields) || customFields.length === 0) return defaultFields;
  return customFields.map((f, i) => ({
    ...(defaultFields[i] || {}),
    ...f,
    key: String(f.key || defaultFields[i]?.key || `field_${i}`),
    label: String(f.label ?? defaultFields[i]?.label ?? ''),
    inputType: String(f.inputType || defaultFields[i]?.inputType || 'text'),
    readOnly: f.readOnly ?? defaultFields[i]?.readOnly ?? false,
    required: f.required ?? defaultFields[i]?.required ?? false,
    rows: Number(f.rows) || defaultFields[i]?.rows || 1,
  }));
}

/** Merge saved typeConfig with type defaults (declarations keep full field list). */
export function resolveTypeConfig(question) {
  const type = String(question?.type || '').toUpperCase();
  const defaults = getDefaultTypeConfig(type);
  const saved = question?.typeConfig && typeof question.typeConfig === 'object' ? question.typeConfig : {};

  if (defaults.fields) {
    return {
      ...defaults,
      ...saved,
      fields: mergeFields(defaults.fields, saved.fields),
    };
  }
  if (defaults.columns) {
    return {
      ...defaults,
      ...saved,
      columns: Array.isArray(saved.columns) && saved.columns.length ? saved.columns : defaults.columns,
    };
  }
  return { ...defaults, ...saved };
}

export function getDeclarationFields(question) {
  const cfg = resolveTypeConfig(question);
  return Array.isArray(cfg.fields) ? cfg.fields : [];
}

export function isDeclarationQuestionType(question) {
  const family = getRenderFamily(question?.type);
  return family === 'declaration-print' || family === 'declaration-consent' || family === 'declaration-custom';
}

/** True when every configured field is read-only (nothing captured online). */
export function isPurePrintDeclaration(question) {
  if (!isDeclarationQuestionType(question)) return false;
  const fields = getDeclarationFields(question);
  if (!fields.length) {
    const cfg = resolveTypeConfig(question);
    return cfg.mode === 'print';
  }
  return fields.every((f) => f.readOnly);
}

/** True when typeConfig.mode is print (print note + hand-fill PDF styling for read-only fields). */
export function isPrintDeclarationMode(question) {
  if (!isDeclarationQuestionType(question)) return false;
  const cfg = resolveTypeConfig(question);
  return cfg.mode === 'print';
}

export function isPrintDeclaration(question) {
  return isPurePrintDeclaration(question);
}

export function isOnlineDeclaration(question) {
  if (!isDeclarationQuestionType(question)) return false;
  return !isPurePrintDeclaration(question);
}

export function applyTypeChangeDefaults(question, nextType) {
  const type = String(nextType || '').toUpperCase();
  const meta = getQuestionTypeMeta(type);
  const next = {
    ...question,
    type,
    typeConfig: getDefaultTypeConfig(type),
  };

  if (!meta.needsOptions && !['LABELED_LIST', 'TABLE_ROWS', 'DATA_GRID', 'COMPARISON_TABLE', 'DROPDOWN_GRID', 'DYNAMIC_TABLE'].includes(type)) {
    next.options = [];
  }
  if (type === 'LABELED_LIST' && (!next.options || next.options.length === 0)) {
    next.options = ['', '', ''];
  }
  if (type === 'DROPDOWN_GRID' && (!next.options || next.options.length === 0)) {
    next.options = Array.from({ length: 6 }, (_, i) => `Row ${i + 1}`);
  }
  if ((type === 'TABLE_ROWS' || type === 'DATA_GRID' || type === 'COMPARISON_TABLE' || type === 'DYNAMIC_TABLE') && (!next.options || next.options.length === 0)) {
    next.options = type === 'TABLE_ROWS' ? ['', ''] : [''];
  }
  if (type === 'SIDE_BY_SIDE_CHOICE' && (!next.options || next.options.length === 0)) {
    next.options = ['', '', ''];
  }
  if (meta.family === 'rating' && !next.validationRules?.max) {
    next.validationRules = { ...(next.validationRules || {}), max: 5 };
  }
  if (type === 'PERCENTAGE') {
    next.validationRules = { ...(next.validationRules || {}), min: 0, max: 100 };
  }
  if (type === 'VIDEO') {
    next.uploadPolicy = {
      maxFiles: 1,
      maxFileSizeMB: 100,
      totalFieldSizeMB: 100,
      allowedMimeGroups: ['video'],
      allowedExtensions: ['mp4'],
      ...(next.uploadPolicy || {}),
    };
    if (!next.helpText) {
      next.helpText = 'Upload video file (MP4 format only).';
    }
  }
  if (type === 'AUDIO') {
    next.uploadPolicy = {
      maxFiles: 1,
      maxFileSizeMB: 50,
      totalFieldSizeMB: 50,
      allowedMimeGroups: ['audio'],
      allowedExtensions: ['mp3'],
      ...(next.uploadPolicy || {}),
    };
    if (!next.helpText) {
      next.helpText = 'Upload audio file (MP3 format only).';
    }
  }
  if (type === 'TEXT_WITH_FILE') {
    next.uploadPolicy = {
      maxFiles: 1,
      maxFileSizeMB: 10,
      totalFieldSizeMB: 25,
      allowedMimeGroups: [],
      ...(next.uploadPolicy || {}),
    };
  }
  return next;
}

export const DECLARATION_FIELD_INPUT_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Long text' },
  { value: 'date', label: 'Date' },
  { value: 'email', label: 'Email' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'signature', label: 'Signature area' },
];
